import { useSelector } from 'react-redux';
import React, { Fragment, ReactElement, ReactNode } from 'react';
import { Product } from '@portal/portal-core';
import { selectLicenseStatus } from 'selectors';
import { NO_ACTION } from 'constants/product-tab';
import { PRODUCT_INFO } from 'constants/products';

import { HollowButton, DownloadButton } from 'components/product-buttons';
import ProductSplash from '../product-splash';

import heroImage from '../images/bdrc-hero.png';

const { NAMES_UI_TABS } = Product;

const NEW_WINDOW_ATTRS = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

interface BDRCTabProps {
  marketingContentOnly?: boolean;
}

interface PageContent {
  header: string;
  description: ReactNode;
  buttons: ReactElement[];
  additionalContent: ReactNode;
}

const BDRCTab = ({ marketingContentOnly = false }: BDRCTabProps): ReactElement | null => {
  const { licenseState } = useSelector(selectLicenseStatus);

  const productButtons = {
    signupButton: (
      <DownloadButton
        {...NEW_WINDOW_ATTRS}
        key="signup"
        text="Sign Up Today"
        href={PRODUCT_INFO[NAMES_UI_TABS.bdrc].links.home}
      />
    ),
    webinarButton: (
      <HollowButton
        {...NEW_WINDOW_ATTRS}
        key="learnmore"
        text="Learn More"
        href={PRODUCT_INFO[NAMES_UI_TABS.bdrc].links.learnMore}
      />
    ),
  };

  const additionalContent = {
    blurbs: (
      <Fragment>
        <div>
          <b>Seemless software integration</b>
          <p>
            Create BD FACSDiva™ Software experiments away from the cytometer and open stored
            analysis files directly in FlowJo™ Software.
          </p>
        </div>
        <div>
          <b>Easily store and share your data</b>
          <p>
            Organize workflows and projects with a specific set of users. Store your data in the
            context of an experiment.
          </p>
        </div>
        <div>
          <b>Safe and secure collaboration</b>
          <p>
            Manage the laboratory workflow with customizable permissions in a secure cloud-based
            ecosystem.
          </p>
        </div>
      </Fragment>
    ),
  };

  const getPageContent = (): PageContent | null => {
    const { signupButton, webinarButton } = productButtons;
    const { blurbs } = additionalContent;

    const headerText = 'Re-imagine how you work with BD® Research Cloud (BD RC)';
    const headerBody =
      'BD RC is a comprehensive ecosystem for research flow cytometry experiments that helps you increase visibility and traceability, collaborate with your colleagues, organize your projects and monitor your experiments using an all-in-one integrated tool.';

    switch (licenseState) {
      case NO_ACTION:
        return {
          header: headerText,
          description: <Fragment>{headerBody}</Fragment>,
          buttons: [signupButton, webinarButton],
          additionalContent: blurbs,
        };
      default:
        return null;
    }
  };

  const pageContent = getPageContent();
  if (!pageContent) {
    return null;
  }
  return (
    <ProductSplash
      header={pageContent.header}
      description={pageContent.description}
      additionalContent={pageContent.additionalContent}
      heroImg={{ src: heroImage, alt: 'BD® Research Cloud preview' }}
      buttons={marketingContentOnly ? null : pageContent.buttons}
      productInfo={{
        name: PRODUCT_INFO[NAMES_UI_TABS.bdrc].name,
        // logoSvg is a non-null svg import for BD RC; coerce the nullable entry type
        logoSvg: PRODUCT_INFO[NAMES_UI_TABS.bdrc].logoSvg ?? '',
      }}
    />
  );
};

export default BDRCTab;
